import React, { useEffect } from 'react';
import {BrowserRouter as Router, Routes, Route, Navigate} from 'react-router-dom';
import {ThemeProvider, createTheme} from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { useKeycloak } from '@react-keycloak/web';
import { Box } from '@mui/material';

// Import components
import LandingPage from './components/LandingPage';
import LoginPage from './components/LoginPage';
import RegisterPage from './components/RegisterPage';
import Dashboard from './components/Dashboard';
import JoinGroup from './components/JoinGroup';
import ErrorBoundary from './components/ErrorBoundary';
import DespHeader from './components/DespHeader';
import DespFooter from './components/DespFooter';

// Import Keycloak Auth Provider
import { KeycloakAuthProvider } from './services/KeycloakAuthService';

// Import axios setup
import { setupAxiosKeycloak } from './services/setupAxios';
import api from './services/api';

// Helper to get environment variables from runtime config or build-time config
const getEnvVar = (key: string, defaultValue: string = ''): string => {
    // Try runtime config first (production)
    if (window.env && window.env[key as keyof typeof window.env]) {
        return window.env[key as keyof typeof window.env] || defaultValue;
    }
    // Fallback to build-time config (development)
    return (process.env[key] as string) || defaultValue;
};

// Modern SenCore theme with enhanced aesthetics inspired by professional platforms
const sencoreTheme = createTheme({
    palette: {
        mode: 'dark',
        primary: {
            main: '#00d4ff',
            light: '#33dcff',
            dark: '#0099cc',
            contrastText: '#000000',
        },
        secondary: {
            main: '#ff6b6b',
            light: '#ff8a80',
            dark: '#d32f2f',
        },
        background: {
            default: '#0A0F1E',
            paper: '#1a1a2e',
        },
        success: {
            main: '#00f5a0',
            light: '#4fffb4',
            dark: '#00c876',
        },
        warning: {
            main: '#ffb800',
            light: '#ffc947',
            dark: '#cc9300',
        },
        error: {
            main: '#ff4757',
            light: '#ff6b7a',
            dark: '#cc1e2d',
        },
        info: {
            main: '#3742fa',
            light: '#5352ed',
            dark: '#2f3542',
        },
        text: {
            primary: '#ffffff',
            secondary: 'rgba(255, 255, 255, 0.7)',
            disabled: 'rgba(255, 255, 255, 0.38)',
        },
        divider: 'rgba(255, 255, 255, 0.12)',
    },
    typography: {
        fontFamily: '"Inter", "SF Pro Display", "Roboto", -apple-system, sans-serif',
        h1: {
            fontWeight: 800,
            fontSize: '2.5rem',
            letterSpacing: '-0.02em',
            background: 'linear-gradient(135deg, #00d4ff 0%, #ff6b6b 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent',
        },
        h2: {
            fontWeight: 700,
            fontSize: '2rem',
            letterSpacing: '-0.01em',
            color: '#ffffff',
        },
        h3: {
            fontWeight: 600,
            fontSize: '1.5rem',
            letterSpacing: '-0.01em',
            color: '#ffffff',
        },
        h4: {
            fontWeight: 600,
            fontSize: '1.25rem',
            color: '#ffffff',
        },
        h5: {
            fontWeight: 500,
            fontSize: '1.125rem',
            color: '#ffffff',
        },
        h6: {
            fontWeight: 500,
            fontSize: '1rem',
            color: '#ffffff',
        },
        body1: {
            fontSize: '1rem',
            lineHeight: 1.6,
            color: 'rgba(255, 255, 255, 0.87)',
        },
        body2: {
            fontSize: '0.875rem',
            lineHeight: 1.5,
            color: 'rgba(255, 255, 255, 0.7)',
        },
    },
    shape: {
        borderRadius: 16,
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    background: 'linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%)',
                    backgroundAttachment: 'fixed',
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    padding: '10px 24px',
                    boxShadow: 'none',
                    '&:hover': {
                        boxShadow: '0 8px 25px -8px rgba(0, 212, 255, 0.3)',
                    },
                },
                contained: {
                    background: 'linear-gradient(135deg, #00d4ff 0%, #0099cc 100%)',
                    '&:hover': {
                        background: 'linear-gradient(135deg, #33dcff 0%, #00b8e6 100%)',
                    },
                },
                outlined: {
                    borderColor: 'rgba(0, 212, 255, 0.5)',
                    color: '#00d4ff',
                    '&:hover': {
                        borderColor: '#00d4ff',
                        backgroundColor: 'rgba(0, 212, 255, 0.04)',
                    },
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 20,
                    background: 'linear-gradient(145deg, #1a1a2e 0%, #16213e 100%)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(20px)',
                    boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    borderRadius: 16,
                    backgroundImage: 'none',
                    backgroundColor: '#1a1a2e',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                },
                elevation1: {
                    boxShadow: '0 4px 16px 0 rgba(0, 0, 0, 0.2)',
                },
                elevation2: {
                    boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.3)',
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backgroundColor: 'rgba(26, 26, 46, 0.8)',
                    backdropFilter: 'blur(20px)',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                    boxShadow: '0 4px 16px 0 rgba(0, 0, 0, 0.1)',
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    fontWeight: 500,
                },
                filled: {
                    backgroundColor: 'rgba(0, 212, 255, 0.1)',
                    color: '#00d4ff',
                    border: '1px solid rgba(0, 212, 255, 0.3)',
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        borderRadius: 12,
                        backgroundColor: 'rgba(255, 255, 255, 0.02)',
                        '&:hover fieldset': {
                            borderColor: 'rgba(0, 212, 255, 0.5)',
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: '#00d4ff',
                            borderWidth: '2px',
                        },
                    },
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '0.95rem',
                    minHeight: 48,
                    '&.Mui-selected': {
                        color: '#00d4ff',
                    },
                },
            },
        },
        MuiTabs: {
            styleOverrides: {
                root: {
                    '& .MuiTabs-indicator': {
                        backgroundColor: '#00d4ff',
                        height: 3,
                        borderRadius: '3px 3px 0 0',
                    },
                },
            },
        },
    },
});

// Protected Route component
interface ProtectedRouteProps {
    children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({children}) => {
    const { keycloak, initialized } = useKeycloak();

    // Wait for Keycloak to initialize
    if (!initialized) {
        return <div>Loading...</div>;
    }

    // Check if authenticated via Keycloak
    const isAuthenticated = keycloak.authenticated;

    if (!isAuthenticated) {
        // Auto-login via Keycloak, preserving the current URL for redirect after auth
        keycloak.login({
            redirectUri: window.location.href
        });
        return <div>Redirecting to login...</div>;
    }

    return <>{children}</>;
};

// Inner App component that uses Keycloak hook
const AppContent: React.FC = () => {
    const { keycloak, initialized } = useKeycloak();
    const [axiosReady, setAxiosReady] = React.useState(false);

    // Setup axios with Keycloak when initialized
    useEffect(() => {
        if (initialized) {
            setupAxiosKeycloak(api, keycloak);

            // Small delay to ensure token is properly set before first API call
            setTimeout(() => {
                setAxiosReady(true);
            }, 100);
        }
    }, [initialized, keycloak]);

    // Wait for both Keycloak and Axios to be ready before rendering app
    if (!initialized || !axiosReady) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <div>Loading authentication...</div>
            </Box>
        );
    }

    // Get user email from Keycloak token
    const userEmail = keycloak.tokenParsed?.email || keycloak.tokenParsed?.preferred_username;

    // Handle login with redirect to dashboard
    const handleLogin = () => {
        // Save current path so we can redirect back after login (e.g., /join/:token)
        const currentPath = window.location.pathname;
        if (currentPath && currentPath !== '/' && currentPath !== '/login') {
            localStorage.setItem('sencore_redirect_after_login', currentPath);
        }
        keycloak.login({
            redirectUri: window.location.origin + '/dashboard'
        });
    };

    // Handle logout
    const handleLogout = () => {
        // Logout from Keycloak
        keycloak.logout({
            redirectUri: window.location.origin
        });
    };

    return (
        <>
            {/* DESP Header - appears on all pages */}
            <DespHeader
                userEmail={userEmail}
                onLogin={handleLogin}
                onLogout={handleLogout}
                portalUrl={getEnvVar('REACT_APP_PORTAL_URL', 'https://platform.destine.eu')}
                iamUrl={getEnvVar('REACT_APP_IAM_URL', 'https://auth.destine.eu')}
                serviceName="sencore"
            />

            {/* Main Content Area with padding for header and footer */}
            <Box sx={{
                minHeight: 'calc(100vh - 128px)', // 64px header + 64px footer
                paddingBottom: '64px', // Space for fixed footer
            }}>
                <Router>
                    <Routes>
                        {/* Public Routes */}
                        <Route path="/" element={<ErrorBoundary><LandingPage onLogin={handleLogin} /></ErrorBoundary>}/>
                        <Route path="/login" element={<ErrorBoundary><LoginPage/></ErrorBoundary>}/>
                        <Route path="/register" element={<ErrorBoundary><RegisterPage/></ErrorBoundary>}/>

                        {/* Protected Routes */}
                        <Route path="/dashboard" element={
                                <ErrorBoundary>
                                    <ProtectedRoute>
                                        <Dashboard/>
                                    </ProtectedRoute>
                                </ErrorBoundary>
                            }
                        />

                        {/* Join Group via invite link */}
                        <Route path="/join/:token" element={
                            <ErrorBoundary>
                                <ProtectedRoute>
                                    <JoinGroup />
                                </ProtectedRoute>
                            </ErrorBoundary>
                        }/>

                        {/* Catch all route - redirect to landing page */}
                        <Route path="*" element={<Navigate to="/" replace/>}/>
                    </Routes>
                </Router>
            </Box>

            {/* DESP Footer - appears on all pages */}
            <DespFooter webPortalUrl="https://destination-earth.eu/" />
        </>
    );
};

// Main App component with Keycloak Provider
const App: React.FC = () => {
    return (
        <ThemeProvider theme={sencoreTheme}>
            <CssBaseline/>
            <KeycloakAuthProvider>
                <AppContent />
            </KeycloakAuthProvider>
        </ThemeProvider>
    );
}

export default App;
